import { useEffect, useMemo, useRef, useState } from "react";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { SupportedSession, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { getLatestBridgeEligibilityAnswer, isBookingBlockedByEligibility } from "../../utils";
import { getBookingAddressState } from "./getBookingAddressState";
import { resolveBookingTags } from "./resolveBookingTags";
import { emitFormAnalyticsEvent } from "../../hooks/emit-form-analytics-event";
import { BookingPageEmbed } from "./BookingPageEmbed";

export interface AppointmentBookingFieldContentProps
  extends Omit<TellescopeFormFieldBaseProps<"Appointment Booking">, "title"> {
  session: SupportedSession;
  enduserId: string;
  userIds?: string[];
  portalURL?: string;
  organizationSubdomain?: string;
  priorResponses?: TellescopeFieldResponse[];
  setBookedAppointmentUrl?: (bookedAppointmentUrl: string | undefined) => void;
  formResponseId?: string;
  logic?: string;
  formId?: string;
  analyticsTitle?: string;
  goToPreviousField?: () => void;
  isPreviousDisabled?: boolean;
}

export const AppointmentBookingFieldContent = ({
  id,
  value,
  onChange,
  className,
  session,
  enduserId,
  options,
  priorResponses,
  setBookedAppointmentUrl,
  formResponseId,
  logic,
  formId,
  analyticsTitle,
  goToPreviousField,
  isPreviousDisabled,
}: AppointmentBookingFieldContentProps) => {
  const { t } = useTranslation("forms");
  const bookedEventReportedRef = useRef(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const addressState = priorResponses?.length ? getBookingAddressState(priorResponses) : undefined;

  const {
    data: bookingInfoData,
    isLoading,
    error,
  } = useTellescopeQuery({
    model: "form_fields",
    method: "booking_info",
    request: {
      bookingPageId: options?.bookingPageId ?? "",
      enduserId,
      ...(addressState ? { enduserFields: { state: addressState } } : {}),
    },
    session,
    enabled: !!options?.bookingPageId,
  });

  const bridgeUserIds = useMemo(() => {
    if (!options?.useBridgeEligibilityResult || !priorResponses?.length) return undefined;
    return getLatestBridgeEligibilityAnswer(priorResponses)?.userIds;
  }, [options?.useBridgeEligibilityResult, priorResponses]);

  // The booking page must not be shown rather than fall back to an unfiltered one: an eligibility
  // check that named no providers means none are bookable, and omitting userIds would offer every
  // provider. The field stays non-blocking — see isBookingBlockedByEligibility.
  const hasNoEligibleProviders = isBookingBlockedByEligibility(
    { type: "Appointment Booking", options },
    priorResponses ?? []
  );

  const iframeURL = useMemo(() => {
    const base = bookingInfoData?.bookingURL ?? "";
    if (!base) return base;

    const params: string[] = [];
    const tagArgs = { responses: priorResponses ?? [], logic };
    // Encoded, unlike legacy, whose raw interpolation broke on any tag holding an `&` or a space.
    const userTags = resolveBookingTags({ tags: options?.userTags ?? [], ...tagArgs });
    if (userTags.length) params.push(`userTags=${encodeURIComponent(userTags.join(","))}`);

    const userFilterTags = resolveBookingTags({ tags: options?.userFilterTags ?? [], ...tagArgs });
    if (userFilterTags.length) params.push(`userFilterTags=${encodeURIComponent(userFilterTags.join(","))}`);

    if (bridgeUserIds?.length) params.push(`userIds=${bridgeUserIds.join(",")}`);
    // The pair is what lets the booking page attribute a held slot to this response, so the id goes
    // with the minutes or neither is sent.
    const holdResponseId = formResponseId || id;
    if (options?.holdAppointmentMinutes && holdResponseId) {
      params.push(`formResponseId=${encodeURIComponent(holdResponseId)}`);
      params.push(`holdAppointmentMinutes=${options.holdAppointmentMinutes}`);
    }
    if (!params.length) return base;

    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${params.join("&")}`;
  }, [
    bookingInfoData?.bookingURL,
    bridgeUserIds,
    options?.holdAppointmentMinutes,
    options?.userTags,
    options?.userFilterTags,
    priorResponses,
    logic,
    formResponseId,
    id,
  ]);

  const bookingURL = bookingInfoData?.bookingURL;
  const entropy = bookingInfoData?.entropy;

  const expectedOrigin = useMemo(() => {
    if (!bookingURL) return undefined;
    try {
      return new URL(bookingURL).origin;
    } catch {
      return undefined;
    }
  }, [bookingURL]);

  // Listen for postMessage from iframe indicating successful booking
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // The nonce only disambiguates between embeds; the origin is what establishes that the
      // message actually came from the booking page we loaded.
      if (expectedOrigin && event.origin !== expectedOrigin) return;

      // Stashed for form.redirectToBookedAppointmentOnSubmit to consume at submit. Arrives as its own
      // message, on its own timing, so it cannot be read off the Booking Success payload below.
      if (event.data?.type === "Join Link" && typeof event.data?.link === "string" && event.data.link) {
        setBookedAppointmentUrl?.(event.data.link);
        return;
      }

      // The booking page has moved to its own confirmation step, which owns the whole frame — the
      // wizard's Back button would compete with it. A picker message means the enduser stepped back
      // out of confirmation, so the two are read as one step indicator rather than a latch.
      if (event.data?.type === "Confirmation") {
        setIsConfirming(true);
        return;
      }
      if (event.data?.type === "CalendarPicker" || event.data?.type === "UsersPicker") {
        setIsConfirming(false);
        return;
      }

      if (event.data?.type !== "Booking Success") return;
      if (typeof event.data?.bookedEventId !== "string") return;
      // The portal echoes an empty nonce for booking URLs minted before entropy existed, so an
      // absent or empty value cannot be attributed and is accepted; a populated one must match.
      if (event.data.entropy && event.data.entropy !== entropy) return;

      onChange(event.data.bookedEventId);

      // Reported once per booking: the portal can re-deliver the success message, and a second event
      // would double-count a conversion in the funnel these events exist to measure.
      if (!bookedEventReportedRef.current) {
        bookedEventReportedRef.current = true;
        emitFormAnalyticsEvent(
          {
            event: "form_progress",
            formId: formId ?? "",
            fieldId: id,
            title: analyticsTitle ?? "",
            previousTitle: "",
            status: "Appointment Booked",
          },
          { category: `form_${formId ?? ""}`, action: "Form Progress", label: analyticsTitle ?? "", value: 1 }
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onChange, expectedOrigin, entropy, setBookedAppointmentUrl, id, formId, analyticsTitle]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <p>{t("Loading appointment booking...")}</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={className} style={{ padding: "2rem", color: "red" }}>
        <p>
          {t("Error: {{errorMessage}}", {
            errorMessage: error instanceof Error ? error.message : t("Failed to load appointment booking"),
          })}
        </p>
      </div>
    );
  }

  // Show success message if appointment was booked
  if (value) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ marginBottom: "1rem", color: "green", fontWeight: "bold" }}>
          {t("✓ Appointment Successfully Booked")}
        </div>
        <p style={{ fontSize: "0.875rem", color: "#666" }}>{t("Event ID: {{id}}", { id: value })}</p>
      </div>
    );
  }

  if (hasNoEligibleProviders) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <p>{t("No eligible users found for booking")}</p>
      </div>
    );
  }

  // Show iframe when URL is ready
  if (iframeURL) {
    // Filter tags without assignment tags means the booking page skipped its user-selection step, so
    // its own back affordance is gone and the enduser has no way back on mobile.
    const showBackButton =
      !!options?.userFilterTags?.length && !options?.userTags?.length && !isPreviousDisabled && !isConfirming;

    return (
      <BookingPageEmbed
        iframeURL={iframeURL}
        className={className}
        warningMessage={bookingInfoData?.warningMessage}
        showBackButton={showBackButton}
        onBack={goToPreviousField}
      />
    );
  }

  return null;
};
